// constants
import { StoreStorageSyncKeys } from 'components/mrp/mrp.consts';

// types
import { EntityStore } from './entityStore.types';

// utils
import dayjs from 'dayjs';
import { createWithEqualityFn } from 'zustand/traditional';
import { requests } from 'RestAPIs/rapini';
import showSnackbar from 'components/shared/Snackbar';
import { getMatchingEntityIdentifiers } from 'components/mrp/mrp.utils';
import { t } from 'components/roster-portal/activity-planning/utils';
import { shallow } from 'zustand/shallow';
import { persist } from 'zustand/middleware';

// Reset the per-subscriber query flags before persisting. Persisting a live
// `isLoading: true` (from a reload mid-fetch) would rehydrate a stuck spinner
// until the next fetch resolves. Returning the same shape keeps the persisted
// type identical to `EntityStore['subscribers']`, so the merge stays type-safe.
const withResetQueryState = (
  subscribers: EntityStore['subscribers']
): EntityStore['subscribers'] =>
  subscribers.map((subscriber) => ({
    ...subscriber,
    isLoading: false,
    isError: false
  }));

export const useEntityStore = createWithEqualityFn<EntityStore>()(
  persist(
    (set, get) => ({
      subscribers: [],
      data: [],
      hasHydrated: false,
      hasAnyLoading: false,
      control: {
        isSubscribed: (planningEntityIdentifiers) => {
          return (
            getMatchingEntityIdentifiers(
              planningEntityIdentifiers,
              get().subscribers
            ).length > 0
          );
        },
        subscribe: (planningEntityIdentifiers) => {
          if (
            !planningEntityIdentifiers ||
            planningEntityIdentifiers.length === 0
          )
            return null;

          set((state) => {
            const newSubscribers = planningEntityIdentifiers.map((pei) => ({
              ...pei,
              isLoading: false,
              isError: false
            }));

            if (shallow(state.subscribers, newSubscribers)) return state;

            if (
              getMatchingEntityIdentifiers(
                planningEntityIdentifiers,
                state.subscribers
              ).length === 0
            ) {
              const uniqueNewSubscribers = newSubscribers.filter(
                (newSub) =>
                  getMatchingEntityIdentifiers([newSub], state.subscribers)
                    .length === 0
              );
              return {
                ...state,
                subscribers: [...state.subscribers, ...uniqueNewSubscribers]
              };
            }
            return state;
          });
        },
        unsubscribe: (planningEntityIdentifiers) => {
          set((state) => {
            if (
              getMatchingEntityIdentifiers(
                planningEntityIdentifiers,
                state.subscribers
              ).length > 0
            ) {
              return {
                ...state,
                subscribers: state.subscribers.filter(
                  (identifier) =>
                    !getMatchingEntityIdentifiers(
                      [identifier],
                      planningEntityIdentifiers
                    )
                )
              };
            }
            return state;
          });
        },
        fetch: async ({
          planningEntityIdentifiers: requested,
          startDate,
          endDate
        }) => {
          const { subscribers } = get();
          const planningEntityIdentifiers = requested || subscribers;

          if (planningEntityIdentifiers.length === 0 || !startDate || !endDate)
            return;

          // Ensure we only update for entities actually subscribed
          const refetchPlanningEntityIdentifiers = getMatchingEntityIdentifiers(
            get().subscribers,
            planningEntityIdentifiers
          ).map(({ entityId }) => entityId);

          set((state) => ({
            ...state,
            subscribers: state.subscribers.map((subscriber) => ({
              ...subscriber,
              ...(refetchPlanningEntityIdentifiers.includes(
                subscriber.entityId
              ) && {
                isLoading: true,
                isError: false
              })
            }))
          }));

          try {
            const response = await requests.getPlanning({
              planningEntityIdentifiers,
              startDate: dayjs(startDate).format('YYYY-MM-DD'),
              endDate: dayjs(endDate).format('YYYY-MM-DD')
            });

            const responsePlanningEntityIdentifiers =
              getMatchingEntityIdentifiers(
                get().subscribers,
                response.planningEntities.map(
                  ({ planningEntityIdentifier }) => planningEntityIdentifier
                )
              ).map(({ entityId }) => entityId);

            // Replace data only for entities that were fetched
            const newEntities = response.planningEntities.filter(
              ({ type }) => type !== 'EMPLOYMENT'
            );

            const fetchedEntityIds = planningEntityIdentifiers.map(
              (identifier) => identifier.entityId
            );

            // Keep data for entities that:
            // 1. Weren't part of this fetch AND
            // 2. Are still subscribed (to prevent unbounded memory growth)
            const currentSubscribers = get().subscribers;
            const existingData = get().data;
            const unfetchedData = existingData.filter(
              (entity) =>
                !fetchedEntityIds.includes(
                  entity.planningEntityIdentifier.entityId
                ) &&
                getMatchingEntityIdentifiers(
                  [entity.planningEntityIdentifier],
                  currentSubscribers
                ).length > 0
            );

            const refreshedData = [...unfetchedData, ...newEntities];

            set((state) => ({
              ...state,
              data: refreshedData,
              subscribers: state.subscribers.map((subscriber) => ({
                ...subscriber,
                ...(responsePlanningEntityIdentifiers.includes(
                  subscriber.entityId
                ) && {
                  isLoading: false,
                  isError: false
                })
              }))
            }));
          } catch {
            showSnackbar({
              severity: 'error',
              message: t('somethingWentWrong')
            });

            set((state) => ({
              ...state,
              subscribers: state.subscribers.map((subscriber) => ({
                ...subscriber,
                ...(refetchPlanningEntityIdentifiers.includes(
                  subscriber.entityId
                ) && {
                  isLoading: false,
                  isError: true
                })
              }))
            }));
          }
        },
        setAnyLoading: (hasAnyLoading: boolean) =>
          set((state) => {
            return {
              ...state,
              hasAnyLoading
            };
          })
      }
    }),
    {
      name: StoreStorageSyncKeys.ENTITY_STORE,
      // Only persist `subscribers` (which entities are open) — small and stable.
      // `data` is intentionally NOT persisted: it holds the full planning payload
      // (all events for every open resource) and is refetched on load anyway
      // (see useRefetchPlanningOnEntityChange). Persisting it could grow past the
      // ~5MB localStorage quota and throw QuotaExceededError, crashing the app.
      partialize: (state) => ({
        subscribers: withResetQueryState(state.subscribers)
      }),
      onRehydrateStorage: (state) => {
        state.hasHydrated = true;
      },
      // v1 blobs still contain the oversized `data` array. `partialize` only
      // governs writes, so bumping the version forces `migrate` to run on read,
      // dropping `data` and rewriting the entry small on the very first load —
      // instead of letting the multi-MB blob linger at quota until the next
      // write (which could itself throw QuotaExceededError from another store).
      version: 2,
      migrate: (persistedState) => ({
        subscribers: withResetQueryState(
          (persistedState as Partial<EntityStore> | undefined)?.subscribers ??
            []
        )
      })
    }
  ),
  shallow
);
